import { forwardRef, type CSSProperties } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import AppIcon from './AppIcon'

import { cn, hexToRgba } from '@/lib/utils'
import type { Media } from '@/payload-types'

type AppStackProps = VariantProps<typeof primaryIcon> &
  VariantProps<typeof secondaryIcon> &
  VariantProps<typeof tertiaryIcon> & {
    icon?: Media | null
    glyph?: Media | null
    name?: string
    slug?: string
    multiple?: boolean
    background?: string | null
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'fluid'
    highlight?: boolean
    invert?: boolean
    className?: string
    style?: CSSProperties
    priority?: boolean
    loading?: 'eager' | 'lazy'
  }

const primaryIcon = cva('overflow-hidden transition-colors duration-500 squircle', {
  variants: {
    size: {
      sm: 'h-24 w-24',
      md: 'h-[120px] w-[120px]',
      lg: 'h-40 w-40',
      xl: 'h-[240px] w-[240px]',
      fluid: 'w-full aspect-square',
    },
  },
})

const secondaryIcon = cva('absolute', {
  variants: {
    size: {
      sm: 'left-[5px] top-[5px]',
      md: 'left-1.5 top-1.5',
      lg: 'left-1.5 top-1.5',
      xl: 'left-3 top-3',
      fluid: 'left-[5%] top-[5%]',
    },
  },
})

const tertiaryIcon = cva('absolute', {
  variants: {
    size: {
      sm: 'left-2.5 top-2.5',
      md: 'left-3 top-3',
      lg: 'left-3 top-3',
      xl: 'left-6 top-6',
      fluid: 'left-[10%] top-[10%]',
    },
  },
})

const AppStack = forwardRef<HTMLDivElement, AppStackProps>(function AppStack(
  {
    icon,
    glyph,
    name,
    slug,
    multiple,
    background,
    size,
    highlight,
    invert,
    className,
    style,
    priority,
    loading,
  },
  ref,
) {
  const resolvedBackground = background ?? '#FFFFFF'

  return (
    <div ref={ref} style={style} className={cn('relative', className)}>
      {multiple && (
        <>
          <AppContainer
            className={cn('z-10 delay-300', tertiaryIcon({ size }))}
            background={resolvedBackground}
            opacity={0.1}
            border={highlight ? 0.2 : 0.05}
            size={size}
            highlight={highlight}
            invert={invert}
          />
          <AppContainer
            className={cn('z-20 delay-100', secondaryIcon({ size }))}
            background={resolvedBackground}
            opacity={0.15}
            border={highlight ? 0.4 : 0.1}
            size={size}
            highlight={highlight}
            invert={invert}
          />
        </>
      )}

      <AppContainer
        className="relative z-30"
        background={resolvedBackground}
        border={highlight ? 1 : 0.25}
        size={size}
        highlight={highlight}
        invert={invert}
      >
        {(glyph || icon) && name && (
          <AppIcon
            icon={icon}
            glyph={glyph}
            name={name}
            slug={slug}
            background={background}
            priority={priority}
            loading={loading}
          />
        )}
      </AppContainer>
    </div>
  )
})

AppStack.displayName = 'AppStack'

export default AppStack

export function AppContainer({
  children,
  className,
  background,
  opacity,
  border,
  size,
  highlight,
  invert,
}: {
  children?: React.ReactNode
  className?: string
  background: string
  opacity?: number
  border?: number
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fluid'
  highlight?: boolean
  invert?: boolean
}) {
  const defaultBorder = invert ? '255, 255, 255' : '0, 0, 0'

  return (
    <div
      className={cn(primaryIcon({ size }), className)}
      style={{
        background: hexToRgba(background, opacity ?? 1),
        border: `0.5px solid rgba(${highlight ? '16, 185, 129' : defaultBorder}, ${border})`,
      }}
    >
      {children}
    </div>
  )
}
